import { Avatar } from '@seek/avatar';
import { useTranslations } from '@vocab/react';
import { Box, Stack, Text, TooltipRenderer } from 'braid-design-system';
import type { ComponentProps, FC } from 'react';

import translations from './.vocab';

import * as styles from './AvatarGroup.css';

type AvatarProps = ComponentProps<typeof Avatar>;

interface AvatarGroupProps {
  id: string;
  names: Array<AvatarProps['name']>;
  size: AvatarProps['size'];
  space: keyof typeof styles.space;
  max?: number;
  loading?: boolean;
}

export const AvatarGroup: FC<AvatarGroupProps> = ({
  id,
  max = 3,
  names,
  size = 'standard',
  space,
  loading,
}) => {
  const { t } = useTranslations(translations);
  const { visibleNames, hiddenNames } = names.reduce<{
    visibleNames: string[];
    hiddenNames: string[];
  }>(
    (prev, quality, index) => {
      if (index < max) {
        prev.visibleNames.push(quality);
      } else {
        prev.hiddenNames.push(quality);
      }

      return prev;
    },
    {
      visibleNames: [],
      hiddenNames: [],
    },
  );

  if (loading) {
    return (
      <Box display="flex">
        <Box
          display="flex"
          flexDirection="rowReverse"
          gap="none"
          alignItems="flexStart"
        >
          {Array.from({ length: 3 })
            .reverse()
            .map((_, index) => (
              <Box
                key={index}
                position="relative"
                display="flex"
                borderRadius="full"
                className={[styles.space[space], styles.border]}
              >
                <Avatar size="xsmall" variant="icon" name="" loading />
              </Box>
            ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex">
      <Box
        display="flex"
        flexDirection="rowReverse"
        gap="none"
        alignItems="flexStart"
      >
        {hiddenNames.length > 0 ? (
          <TooltipRenderer
            id={`${id}-extra-avatar-tooltip`}
            tooltip={
              <Stack space="xsmall" align="center">
                {hiddenNames.map((name) => (
                  <Text key={name} weight="strong" size="small">
                    {name}
                  </Text>
                ))}
              </Stack>
            }
          >
            {({ triggerProps }) => (
              <Box
                className={[styles.space[space], styles.border]}
                aria-hidden
                borderRadius="full"
                {...triggerProps}
              >
                <Box
                  className={styles.avatar[size]}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                >
                  <Text size={size} weight="strong">
                    {t('+{count}', { count: hiddenNames.length })}
                  </Text>
                </Box>
              </Box>
            )}
          </TooltipRenderer>
        ) : null}
        {visibleNames.reverse().map((name) => (
          <TooltipRenderer
            key={name}
            id={`${id}-${name}-tooltip`}
            tooltip={
              <Text weight="strong" size="small">
                {name}
              </Text>
            }
          >
            {({ triggerProps }) => (
              <Box
                key={name}
                position="relative"
                display="flex"
                borderRadius="full"
                className={[styles.space[space], styles.border]}
                {...triggerProps}
              >
                <Avatar name={name} variant="initials" size={size} />
              </Box>
            )}
          </TooltipRenderer>
        ))}
      </Box>
    </Box>
  );
};
